import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiArrowsInSimpleLight, PiArrowsOutSimpleLight } from 'react-icons/pi';
import FieldShell, { fieldSurface, FOCUS_RING_ONLY } from './FieldShell';
import { cn } from './Input';
import { containsEmoji, containsDisallowedCharacters } from '../../../utils/validation/validators';
import { removeEmojis, removeSpecialSymbols } from '../../../utils/validation/sanitizers';

interface TextareaProps {
  id: string;
  name?: string;
  label: string;
  placeholder?: string;
  value?: string;
  rows?: number;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  error?: string;
  description?: string;
  italicTxt?: string;
  h?: string;
  icon?: React.ReactNode;
  className?: string;
  allowEmojis?: boolean;
  sanitizeEmojis?: boolean;
  required?: boolean;
  /**
   * Grows the field to a full writing surface on demand.
   *
   * A long piece of prose - a matter summary, a case note - is written and read
   * through the same four-row box every short field gets, so it is scrolled
   * rather than seen. `expandable` puts an expand/collapse control on the label
   * row: collapsed is exactly the height it was, expanded is a tall pane. The
   * browser's own resize grip stays live in both states, so a size between the
   * two is still reachable by dragging.
   */
  expandable?: boolean;
  /** Start expanded. Only meaningful with `expandable`. */
  defaultExpanded?: boolean;
  /** Height while expanded. */
  expandedHeight?: string;
}

const Textarea: React.FC<TextareaProps> = ({
  id,
  name,
  label,
  placeholder,
  value,
  rows = 4,
  onChange,
  disabled,
  error,
  description,
  italicTxt,
  className,
  h,
  icon,
  allowEmojis = false,
  sanitizeEmojis = true,
  required = false,
  expandable = false,
  defaultExpanded = false,
  expandedHeight = 'min-h-[24rem]',
}) => {
  const { t } = useTranslation();
  const [emojiError, setEmojiError] = useState<string>();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value;

    // Check for emojis if not allowed
    if (!allowEmojis && sanitizeEmojis && newValue) {
      if (containsEmoji(newValue)) {
        setEmojiError(t('Emojis are not allowed in this field'));
        // Auto-remove emojis
        newValue = removeEmojis(newValue);
      } else if (containsDisallowedCharacters(newValue)) {
        setEmojiError(t('Invalid characters detected'));
        newValue = removeSpecialSymbols(newValue);
      } else {
        setEmojiError(undefined);
      }

      // Update the textarea value to the sanitized version
      e.target.value = newValue;
    }

    // Call the original onChange handler
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      description={description}
      italicText={italicTxt}
      error={emojiError || error}
      className="w-full"
      labelSuffix={expandable ? (
        <button
          type="button"
          data-testid="textarea-expand"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          title={expanded ? t('Collapse') : t('Expand')}
          className={clsx(
            'ms-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5',
            'text-[11px] font-medium text-fg-muted hover:text-fg-strong',
            'transition-colors duration-200 ease-slide',
            FOCUS_RING_ONLY
          )}
        >
          {expanded
            ? <PiArrowsInSimpleLight className="h-3.5 w-3.5" aria-hidden />
            : <PiArrowsOutSimpleLight className="h-3.5 w-3.5" aria-hidden />}
          {expanded ? t('Collapse') : t('Expand')}
        </button>
      ) : undefined}
    >
      <div className="relative">
        {icon && <div className="absolute start-3 top-2.5 text-fg-muted">{icon}</div>}
        <textarea
          id={id}
          name={name}
          aria-invalid={error || emojiError ? true : undefined}
          // A field shown without a visible label (the section heading says
          // what it is) still needs a name for screen readers.
          aria-label={label ? undefined : placeholder}
          data-expanded={expandable ? expanded : undefined}
          className={cn(
            fieldSurface({ error: !!(error || emojiError), disabled, height: false }),
            // py-2.5, not py-1.5: at 6px the first line sat against the top edge
            // of the box, which read as a rendering fault rather than a field.
            'block px-3 py-2.5',
            icon ? 'ps-10' : 'ps-3',
            expandable && 'transition-[min-height] duration-300 ease-slide',
            // `h` is an explicit height from the caller and must beat the
            // default - but an expanded field beats both.
            expanded ? expandedHeight : (h || 'min-h-[80px]'),
            className
          )}
          placeholder={placeholder}
          value={value}
          rows={rows}
          onChange={handleChange}
          disabled={disabled}
        />
      </div>
    </FieldShell>
  );
};

export default Textarea;
